import React, { useState } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  Check,
  LoaderCircle,
  MapPin,
  Send,
  ShieldCheck,
  Star,
} from "lucide-react";
import "./RideMeSPA.css";

const vehicles = [
  {
    name: "Everyday sedans",
    description:
      "Easy-going, comfortable rides for errands, workdays, and getting across town.",
    badge: "A familiar favourite",
    image: "/toyota-corolla.jpg",
    alt: "A white everyday sedan on a coastal road",
  },
  {
    name: "Roomy SUVs",
    description:
      "A little more space for friends, family, luggage, and the long way round.",
    badge: "Made for the weekend",
    image: "/toyota-rav4.jpg",
    alt: "A roomy white SUV parked beneath palm trees",
  },
];

const RideMeSPA = () => {
  const [isSending, setIsSending] = useState(false);
  const [contactStatus, setContactStatus] = useState(null);

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setIsSending(true);
    setContactStatus(null);

    const form = event.currentTarget;
    const formValues = new FormData(form);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.fromEntries(formValues.entries())),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Your message could not be sent. Please try again.");
      }

      form.reset();
      setContactStatus({
        type: "success",
        message: "Thanks for reaching out. The RideMe team will be in touch soon.",
      });
    } catch (error) {
      setContactStatus({
        type: "error",
        message: error.message || "Your message could not be sent. Please try again.",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="rideme-site">
      <header className="site-header">
        <a className="brand header-brand" href="#home" aria-label="RideMe home">
          <img src="/logo.png" alt="" />
        </a>

        <nav className="main-nav" aria-label="Main navigation">
          <a href="https://app.ride-me.app" target="_blank" rel="noopener noreferrer">
            Explore cars
          </a>
          <a href="#how-it-works">How it works</a>
          <a href="#list-your-car">List your car</a>
          <a href="#contact">Get in touch</a>
        </nav>

        <a className="header-cta" href="#download">
          Get the app <ArrowUpRight size={16} strokeWidth={2.2} />
        </a>
      </header>

      <main>
        <section className="hero" id="home">
          <div className="hero-copy">
            <p className="eyebrow">
              <span className="eyebrow-dot" />
              Made for getting around Ghana
            </p>
            <h1>
              The keys to
              <br />
              your next
              <br />
              <span>story.</span>
            </h1>
            <p className="hero-description">
              From the bustle of Accra to a weekend by the coast, find a
              familiar car that fits your plans and your pace.
            </p>
            <div className="hero-actions">
              <a
                className="button button-dark"
                href="https://app.ride-me.app"
                target="_blank"
                rel="noopener noreferrer"
              >
                Find your ride <ArrowRight size={17} />
              </a>
              <a className="text-link" href="#how-it-works">
                See how it works <ArrowDown size={15} />
              </a>
            </div>
            <div className="hero-note">
              <div className="avatar-stack" aria-hidden="true">
                <span>KA</span>
                <span>AM</span>
                <span>JO</span>
              </div>
              <p>
                <strong>Good people, great cars.</strong>
                <br />
                The easier way to get around Ghana.
              </p>
            </div>
          </div>

          <div className="hero-visual" aria-label="A familiar car, ready for a weekend trip">
            <div className="visual-orbit orbit-one" />
            <div className="visual-orbit orbit-two" />
            <div className="sun-disc" />
            <div className="hero-car">
              <img
                src="/cape-coast.jpg"
                alt="Cape Coast shoreline and historic fort in Ghana"
              />
            </div>
            <div className="floating-tag tag-top">
              <span className="tag-icon"><ShieldCheck size={17} /></span>
              <span><strong>Every trip, covered</strong><small>Drive with peace of mind</small></span>
            </div>
            <div className="floating-tag tag-bottom">
              <span className="rating-star"><Star size={15} fill="currentColor" /></span>
              <span><strong>Made for the moment</strong><small>Pick a car. Make a memory.</small></span>
              <ArrowUpRight className="tag-arrow" size={17} />
            </div>
            <span className="visual-caption">01 / A WEEKEND ON THE COAST</span>
          </div>
        </section>

        <section className="cars-section section-wrap" id="cars">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Everyday rides for Ghanaian days</p>
              <h2>Room for every <span>plan.</span></h2>
            </div>
            <a
              className="text-link all-cars-link"
              href="https://app.ride-me.app"
              target="_blank"
              rel="noopener noreferrer"
            >
              Browse all rides <ArrowUpRight size={16} />
            </a>
          </div>
          <div className="vehicle-grid">
            {vehicles.map((vehicle) => (
              <article className="vehicle-card" key={vehicle.name}>
                <a
                  className="vehicle-image"
                  href="https://app.ride-me.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Browse ${vehicle.name} on RideMe`}
                >
                  <img src={vehicle.image} alt={vehicle.alt} loading="lazy" />
                  <span className="vehicle-badge">{vehicle.badge}</span>
                  <span className="vehicle-image-arrow"><ArrowUpRight size={17} /></span>
                </a>
                <div className="vehicle-info">
                  <h3>{vehicle.name}</h3>
                  <p>{vehicle.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="how-section" id="how-it-works">
          <div className="how-intro">
            <p className="eyebrow">Goodbye, complicated</p>
            <h2>Three steps.<br />Then <span>you're off.</span></h2>
            <p>Skip the queues and the paperwork marathon. Your next drive comes together in a few taps.</p>
            <a className="button button-light" href="#download">
              Get started <ArrowRight size={17} />
            </a>
          </div>
          <div className="steps-list">
            <article className="step-card">
              <span className="step-number">01</span>
              <div className="step-icon"><MapPin size={20} /></div>
              <div><h3>Choose your car</h3><p>Browse great local rides and find the one that feels right.</p></div>
              <Check className="step-check" size={19} />
            </article>
            <article className="step-card">
              <span className="step-number">02</span>
              <div className="step-icon"><CalendarDays size={20} /></div>
              <div><h3>Book your dates</h3><p>Pick the time and place. Your host will take it from there.</p></div>
              <Check className="step-check" size={19} />
            </article>
            <article className="step-card">
              <span className="step-number">03</span>
              <div className="step-icon"><ArrowRight size={20} /></div>
              <div><h3>Get out there</h3><p>Meet your host, grab the keys, and make the day yours.</p></div>
              <Check className="step-check" size={19} />
            </article>
          </div>
        </section>

        <section className="host-section section-wrap" id="list-your-car">
          <div className="host-art">
            <div className="host-circle">
              <span className="host-sparkle">✳</span>
              <span className="host-art-word">YOUR CAR<br />HAS PLACES<br />TO BE.</span>
              <span className="host-art-label">MAKE EVERY DAY COUNT ↗</span>
            </div>
            <span className="host-sticker">GOOD<br />THINGS<br />MOVE.</span>
          </div>
          <div className="host-copy">
            <p className="eyebrow">For the car owners</p>
            <h2>Let your car<br />do <span>more.</span></h2>
            <p>
              Listing happens in the RideMe app. Here’s what to expect when you
              add your car:
            </p>
            <ol className="host-listing-steps">
              <li>
                <span className="host-step-number">01</span>
                <span>
                  <strong>Create your account and verify your identity</strong>
                  <small>Complete the profile checks in the app before listing.</small>
                </span>
              </li>
              <li>
                <span className="host-step-number">02</span>
                <span>
                  <strong>Add your car details and documents</strong>
                  <small>Include its make, model, condition, clear photos, and required vehicle documents.</small>
                </span>
              </li>
              <li>
                <span className="host-step-number">03</span>
                <span>
                  <strong>Set your price and availability</strong>
                  <small>Choose your rental rate, available dates, and pick-up details.</small>
                </span>
              </li>
              <li>
                <span className="host-step-number">04</span>
                <span>
                  <strong>Submit your listing for review</strong>
                  <small>Make any requested updates; your listing goes live once approved.</small>
                </span>
              </li>
            </ol>
            <a className="button button-dark host-app-link" href="#download">
              Get the app to list your car <ArrowRight size={16} />
            </a>
          </div>
        </section>

        <section className="contact-section section-wrap" id="contact">
          <div className="contact-copy">
            <p className="eyebrow">We’re here to help</p>
            <h2>Let’s talk<br /><span>things through.</span></h2>
            <p>
              Questions about a trip, the app, or listing your car? Send us a
              note and our team in Ghana will get back to you.
            </p>
          </div>
          <form className="contact-form" onSubmit={handleContactSubmit}>
            <div className="contact-form-row">
              <label>
                Your name
                <input name="name" type="text" autoComplete="name" required maxLength={100} />
              </label>
              <label>
                Email address
                <input name="email" type="email" autoComplete="email" required maxLength={254} />
              </label>
            </div>
            <label>
              What’s this about?
              <select name="subject" defaultValue="General enquiry">
                <option>General enquiry</option>
                <option>Booking support</option>
                <option>List my car</option>
                <option>Feedback</option>
              </select>
            </label>
            <label>
              Your message
              <textarea name="message" rows="4" required minLength={10} maxLength={4000} />
            </label>
            <label className="contact-honeypot" aria-hidden="true">
              Leave this field empty
              <input name="website" tabIndex="-1" autoComplete="off" />
            </label>
            {contactStatus && (
              <p
                className={`contact-status contact-status-${contactStatus.type}`}
                role={contactStatus.type === "error" ? "alert" : "status"}
              >
                {contactStatus.message}
              </p>
            )}
            <button className="button button-dark contact-submit" type="submit" disabled={isSending}>
              {isSending ? (
                <>Sending <LoaderCircle className="contact-spinner" size={16} /></>
              ) : (
                <>Send your message <Send size={15} /></>
              )}
            </button>
          </form>
        </section>

        <section className="download-section section-wrap" id="download">
          <div className="download-copy">
            <p className="eyebrow">Your next ride is a tap away</p>
            <h2>Out there<br />looks <span>good on you.</span></h2>
            <p>Get the RideMe app. Find a car, make a plan, and take the scenic route for once.</p>
            <div className="store-buttons">
              <a href="https://apps.apple.com/gb/app/rideme-affordable-car-rentals/id6714450955" target="_blank" rel="noopener noreferrer">
                <img src="/app-store.png" alt="Download on the App Store" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.caxi.rent" target="_blank" rel="noopener noreferrer">
                <img src="/google-play.png" alt="Get it on Google Play" />
              </a>
            </div>
          </div>
          <div className="app-preview-wrap">
            <div className="app-preview-glow" />
            <img src="/toyota-rav4.jpg" alt="A practical SUV ready for a weekend trip" loading="lazy" />
            <div className="app-preview-note"><span><Check size={15} /></span>Book your next trip in a few taps</div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <a className="brand footer-brand" href="#home">
          <img src="/logo.png" alt="" />
        </a>
        <p>Good rides. Good people. Better days.</p>
        <div className="footer-links">
          <a href="https://www.instagram.com/rideme.gh/" target="_blank" rel="noopener noreferrer">Instagram <ArrowUpRight size={14} /></a>
          <a href="#contact">Get in touch</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
        </div>
        <span className="copyright">© {new Date().getFullYear()} RideMe · Accra, Ghana</span>
      </footer>
    </div>
  );
};

export default RideMeSPA;
